import * as THREE from "three";
import type { InteractionSystem } from "../xr/input";
import type { XRRig } from "../xr/session";
import type { Trainer } from "../trainer/Trainer";
import type { CircuitRole, TrainerSceneRoots } from "../world/station";
import { createGrabbable, type Grabbable } from "./grabbable";
import {
  createPlacementTarget,
  DEFAULT_MOUNT_SNAP_RADIUS,
  DEFAULT_WIRE_SNAP_RADIUS,
  type PlacementTarget,
} from "./placement";
import { GrabManager, type PlaceOutcome } from "./GrabManager";

const ROLES: CircuitRole[] = ["hot", "neutral", "ground"];

/**
 * Domain interaction for the outlet wiring trainer.
 *
 * Registers station wires / terminals / receptacle / box with {@link GrabManager},
 * gates receptacle grab until all wires lock, and advances trainer steps:
 * - all 3 wires locked → `completeStep('place_wires')`
 * - receptacle mounted → `completeStep('mount_receptacle')`
 */
export class WiringTrainerInteraction {
  private readonly trainer: Trainer;
  private readonly grabs: GrabManager;
  private readonly wireByRole = new Map<CircuitRole, Grabbable>();
  private readonly terminalByRole = new Map<CircuitRole, PlacementTarget>();
  private receptacleGrab: Grabbable | null = null;
  private boxTarget: PlacementTarget | null = null;
  private readonly lockedWires = new Set<CircuitRole>();
  private receptacleMounted = false;

  constructor(
    rig: XRRig,
    interaction: InteractionSystem,
    trainer: Trainer,
    station: TrainerSceneRoots,
  ) {
    this.trainer = trainer;
    this.grabs = new GrabManager({
      rig,
      interaction,
      onPlaceOutcome: (o) => this.onPlaceOutcome(o),
    });

    this.registerWiresAndTerminals(station);
    this.registerReceptacleAndBox(station);

    // Step is assigned after reset hooks — keep receptacle gating in sync.
    trainer.subscribe(() => this.syncReceptacleEnabled());
    trainer.onReset(() => this.onTrainerReset());
    this.syncReceptacleEnabled();
  }

  /** Per-frame settle / desktop follow / feedback. Call from main. */
  update(dt: number): void {
    this.grabs.update(dt);
  }

  /**
   * Force-drop held objects without snap (session start/end). Does not clear
   * locked placements — only trainer reset restores those.
   */
  releaseSessionHolds(): void {
    this.grabs.reset();
  }

  private registerWiresAndTerminals(station: TrainerSceneRoots): void {
    // Wire tip local offset: tip mesh sits at x ≈ -0.14; snap so tip meets post.
    const wireSnapPos = new THREE.Vector3(0.14, 0.01, 0);

    for (const role of ROLES) {
      const wireObj = station.wires.getObjectByName(`wire-${role}`);
      if (!wireObj) {
        console.warn(`[WiringTrainer] missing wire-${role}`);
        continue;
      }
      const tip =
        wireObj.getObjectByName(`wire-${role}-tip`) ?? wireObj;

      const grabbable = createGrabbable({
        id: `wire-${role}`,
        object: wireObj,
        placeAnchor: tip,
      });
      this.wireByRole.set(role, grabbable);
      this.grabs.addGrabbable(grabbable);

      const terminalObj = station.terminals.getObjectByName(`terminal-${role}`);
      if (!terminalObj) {
        console.warn(`[WiringTrainer] missing terminal-${role}`);
        continue;
      }

      const target = createPlacementTarget({
        id: `terminal-${role}`,
        object: terminalObj,
        accepts: role,
        snapRadius: DEFAULT_WIRE_SNAP_RADIUS,
        snapLocalPosition: wireSnapPos,
        snapLocalQuaternion: new THREE.Quaternion(),
      });
      this.terminalByRole.set(role, target);
      this.grabs.addTarget(target);
    }
  }

  private registerReceptacleAndBox(station: TrainerSceneRoots): void {
    const { receptacle, electricalBox } = station;

    this.receptacleGrab = createGrabbable({
      id: "receptacle",
      object: receptacle,
      enabled: false, // gated until wires complete
    });
    this.grabs.addGrabbable(this.receptacleGrab);

    // Seat receptacle in the open face of the box (slight +Z toward learner).
    this.boxTarget = createPlacementTarget({
      id: "electrical-box",
      object: electricalBox,
      accepts: "receptacle",
      snapRadius: DEFAULT_MOUNT_SNAP_RADIUS,
      snapLocalPosition: new THREE.Vector3(0, 0, 0.012),
      snapLocalQuaternion: new THREE.Quaternion(),
    });
    this.grabs.addTarget(this.boxTarget);
  }

  private onPlaceOutcome(outcome: PlaceOutcome): void {
    if (outcome.kind === "locked") {
      const { grabbable, target } = outcome;
      const wireId = grabbable.object.userData.wireId as CircuitRole | undefined;
      if (wireId && ROLES.includes(wireId) && target.accepts === wireId) {
        this.lockedWires.add(wireId);
        this.checkWiresComplete();
        return;
      }
      if (
        grabbable.object.userData.kind === "receptacle" &&
        target.accepts === "receptacle"
      ) {
        this.receptacleMounted = true;
        this.trainer.completeStep("mount_receptacle");
      }
      return;
    }

    // Rejected / dropped — no step change. Receptacle stays gated by step.
  }

  private checkWiresComplete(): void {
    if (this.lockedWires.size < ROLES.length) return;
    if (!this.trainer.isCurrent("place_wires")) return;
    const advanced = this.trainer.completeStep("place_wires");
    if (advanced) this.syncReceptacleEnabled();
  }

  private syncReceptacleEnabled(): void {
    if (!this.receptacleGrab) return;
    const canMount =
      this.trainer.hasReached("mount_receptacle") && !this.receptacleMounted;
    // hasReached(mount) is true once place_wires completed (current is mount).
    this.receptacleGrab.enabled =
      canMount && this.trainer.isCurrent("mount_receptacle");
    if (this.receptacleMounted) {
      this.receptacleGrab.enabled = false;
      this.receptacleGrab.locked = true;
    }
  }

  private onTrainerReset(): void {
    this.lockedWires.clear();
    this.receptacleMounted = false;
    // Wires re-enabled; receptacle stays gated until place_wires completes again.
    this.grabs.restoreAll((g) => g.id !== "receptacle");
    if (this.receptacleGrab) {
      this.receptacleGrab.enabled = false;
      this.receptacleGrab.locked = false;
    }
    // syncReceptacleEnabled runs again via trainer.subscribe after step resets.
  }
}

/** Factory matching the suggested main.ts orchestration. */
export function createWiringTrainerInteraction(
  rig: XRRig,
  interaction: InteractionSystem,
  trainer: Trainer,
  station: TrainerSceneRoots,
): WiringTrainerInteraction {
  return new WiringTrainerInteraction(rig, interaction, trainer, station);
}
